// src/utils/subscriptionUtils.js

/**
 * Format subscription status for display
 */
export const formatSubscriptionStatus = (subscription) => {
  if (!subscription) return { label: 'No Subscription', color: 'gray' };

  const statusMap = {
    active: { label: 'Active', color: 'green' },
    trial: { label: 'Trial', color: 'blue' },
    expired: { label: 'Expired', color: 'red' },
    cancelled: { label: 'Cancelled', color: 'orange' },
    suspended: { label: 'Suspended', color: 'yellow' },
  };

  return statusMap[subscription.status] || { label: subscription.status, color: 'gray' };
};

/**
 * Calculate days remaining in trial
 */
export const getTrialDaysRemaining = (subscription) => {
  if (!subscription || subscription.status !== 'trial' || !subscription.trialEndsAt) {
    return 0;
  }

  const now = new Date();
  const trialEnd = new Date(subscription.trialEndsAt);
  const diffTime = trialEnd - now;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return Math.max(0, diffDays);
};

/**
 * Format usage display
 */
export const formatUsage = (usage, limit) => {
  if (limit === -1) return 'Unlimited';
  return `${usage} / ${limit}`;
};

/**
 * Check if upgrade is required for resource
 */
export const isUpgradeRequired = (usage) => {
  return !usage.isUnlimited && usage.percentage >= 100;
};

/**
 * Format price with currency
 */
export const formatPrice = (price, currency = 'LKR') => {
  if (parseInt(price) === 0) return 'Free';
//   return `${currency} ${price.toLocaleString()}`;

  return new Intl.NumberFormat('en-US', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price)+'/-';
};

/**
 * Get days remaining until date
 */
export const getDaysRemaining = (date) => {
  if (!date) return null;
  
  const now = new Date();
  const target = new Date(date);
  const diffTime = target - now;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  return diffDays;
};

/**
 * Check if plan is downgrade from current plan
 */
export const isDowngrade = (currentPlanPrice, newPlanPrice) => {
  return newPlanPrice < currentPlanPrice;
};

/**
 * Check if plan is upgrade from current plan
 */
export const isUpgrade = (currentPlanPrice, newPlanPrice) => {
  return newPlanPrice > currentPlanPrice;
};

/**
 * Get usage percentage
 */
export const getUsagePercentage = (usage, limit) => {
  if (limit === -1) return 0; // Unlimited
  if (limit === 0) return 100;
  return Math.round((usage / limit) * 100);
};

/**
 * Check if usage exceeds limit for any resource
 */
export const hasUsageExceedingLimit = (usage, newPlanLimits) => {
  if (!usage || !newPlanLimits) return false;

  const resourceTypes = ['products', 'shops', 'ecomOrders', 'users', 'warehouses'];

  for (const resourceType of resourceTypes) {
    const currentUsage = usage[resourceType];
    const newLimit = newPlanLimits.find(
      limit => limit.limitType === resourceType && limit.period === 'total'
    );

    if (newLimit && newLimit.limitValue !== -1) {
      if (currentUsage && currentUsage.usage > newLimit.limitValue) {
        return true;
      }
    }
  }

  return false;
};

/**
 * Format billing cycle for display
 */
export const formatBillingCycle = (cycle) => {
  const cycleMap = {
    monthly: 'Monthly',
    quarterly: 'Quarterly',
    yearly: 'Yearly'
  };

  return cycleMap[cycle] || cycle;
};

/**
 * Calculate price for billing cycle
 */
export const calculateCyclePrice = (monthlyPrice, billingCycle) => {
  const multiplier = {
    monthly: 1,
    quarterly: 3,
    yearly: 12
  };

  return monthlyPrice * (multiplier[billingCycle] || 1);
};

/**
 * Format usage percentage with color coding
 * @param {number} usage - Current usage count
 * @param {number} limit - Usage limit (-1 for unlimited)
 * @returns {Object} { color, percentage }
 */
export const formatUsagePercentage = (usage, limit) => {
  if (limit === -1) {
    return { color: '#10b981', percentage: 0 };
  }

  if (limit === 0) {
    return { color: '#ef4444', percentage: 100 };
  }

  const percentage = Math.round((usage / limit) * 100);

  let color;
  if (percentage >= 90) {
    color = '#ef4444'; // Red for critical (>90%)
  } else if (percentage >= 80) {
    color = '#f59e0b'; // Yellow/orange for warning (80-90%)
  } else {
    color = '#10b981'; // Green for good (<80%)
  }

  return { color, percentage };
};

/**
 * Get usage severity level based on percentage
 * @param {number} percentage - Usage percentage
 * @returns {string} 'critical' | 'warning' | 'info'
 */
export const getUsageSeverity = (percentage) => {
  if (percentage >= 90) return 'critical';
  if (percentage >= 80) return 'warning';
  return 'info';
};

/**
 * Format usage display text
 * @param {number} usage - Current usage
 * @param {number} limit - Usage limit
 * @returns {string} Formatted usage string
 */
export const formatUsageDisplay = (usage, limit) => {
  if (limit === -1) return 'Unlimited';
  return `${usage} / ${limit}`;
};

/**
 * Check if resource usage is approaching limit
 * @param {number} usage - Current usage
 * @param {number} limit - Usage limit
 * @param {number} threshold - Threshold percentage (default 80)
 * @returns {boolean}
 */
export const isApproachingLimit = (usage, limit, threshold = 80) => {
  if (limit === -1) return false;
  if (limit === 0) return true;
  
  const percentage = (usage / limit) * 100;
  return percentage >= threshold;
};

/**
 * Check if resource usage has exceeded limit
 * @param {number} usage - Current usage
 * @param {number} limit - Usage limit
 * @returns {boolean}
 */
export const hasExceededLimit = (usage, limit) => {
  if (limit === -1) return false;
  return usage >= limit;
};

/**
 * Get remaining usage count
 * @param {number} usage - Current usage
 * @param {number} limit - Usage limit
 * @returns {number} Remaining count (or -1 for unlimited)
 */
export const getRemainingUsage = (usage, limit) => {
  if (limit === -1) return -1;
  return Math.max(0, limit - usage);
};

/**
 * Get effective price from subscription (handles custom pricing)
 * @param {Object} subscription - Subscription object
 * @returns {number} Effective price
 */
export const getEffectivePrice = (subscription) => {
  if (!subscription) return 0;
  
  // Use effectivePrice if available (from API)
  if (subscription.effectivePrice !== undefined && subscription.effectivePrice !== null) {
    return parseFloat(subscription.effectivePrice);
  }
  
  // Fallback to customPrice if set
  if (subscription.customPrice !== undefined && subscription.customPrice !== null) {
    return parseFloat(subscription.customPrice);
  }
  
  // Fallback to plan price
  return parseFloat(subscription.plan?.price || 0);
};

/**
 * Check if subscription has custom pricing
 * @param {Object} subscription - Subscription object
 * @returns {boolean}
 */
export const hasCustomPricing = (subscription) => {
  if (!subscription) return false;
  
  // Check hasCustomPricing flag if available
  if (subscription.hasCustomPricing !== undefined) {
    return subscription.hasCustomPricing;
  }
  
  // Check if customPrice is set
  return subscription.customPrice !== undefined && 
         subscription.customPrice !== null;
};

/**
 * Calculate discount percentage for custom pricing
 * @param {number} originalPrice - Original plan price
 * @param {number} customPrice - Custom/effective price
 * @returns {number} Discount percentage
 */
export const calculateDiscountPercentage = (originalPrice, customPrice) => {
  if (originalPrice <= 0 || customPrice >= originalPrice) return 0;
  
  const discount = originalPrice - customPrice;
  return Math.round((discount / originalPrice) * 100);
};

/**
 * Format discount display
 * @param {number} percentage - Discount percentage
 * @returns {string} Formatted discount string
 */
export const formatDiscount = (percentage) => {
  if (percentage <= 0) return '';
  return `${percentage}% off`;
};

/**
 * Check if plan requires contact sales
 * @param {Object} plan - Plan object
 * @returns {boolean}
 */
export const isContactSalesPlan = (plan) => {
  return plan && (plan.requiresContactSales === true);
};